import {
  FETCH_MY_SCHOOLS,
  FetchMySchoolsQuery,
  FetchMySchoolsQueryVariables,
  School,
} from '@edwin-group/people-graphql';
import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';

export const useSchoolContext = () => {
  const { data } = useQuery<FetchMySchoolsQuery, FetchMySchoolsQueryVariables>(FETCH_MY_SCHOOLS);
  const { authentication, schoolContext, updateSchoolContext } = useAuthentication();

  const mySchools = useMemo(() => (data?.fetchMySchools || []) as School[], [data]);
  const animationProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -20, height: 0 },
      animate: { opacity: 1, y: 0, height: 'auto' },
      exit: { opacity: 0, y: -20, height: 0 },
      transition: { duration: 0.3, ease: 'easeInOut' },
      style: { overflow: 'hidden' },
    }),
    [],
  );

  const handleSchoolChange = useCallback(
    async (schoolId: string) => {
      updateSchoolContext(mySchools.find((school) => school.documentId === schoolId)!);
      window.location.href = '/';
    },
    [mySchools, updateSchoolContext],
  );

  const shouldShowSchoolSelection = useMemo(
    () => authentication?.user?.access_control_list?.isMAT || authentication?.user?.access_control_list?.isInstructor,
    [authentication?.user?.access_control_list?.isMAT, authentication?.user?.access_control_list?.isInstructor],
  );

  const matLogo = useMemo(() => {
    if (!schoolContext) return null;

    if (schoolContext.logoMAT?.url) {
      return schoolContext.logoMAT;
    }

    if (authentication?.user?.access_control_list?.isMAT && authentication?.user?.school?.documentId) {
      const matSchool = mySchools.find((school) => school.documentId === authentication.user.school?.documentId);

      if (matSchool?.logoMAT?.url) {
        return matSchool.logoMAT;
      }
    }

    return null;
  }, [
    schoolContext,
    authentication?.user?.access_control_list?.isMAT,
    authentication?.user?.school?.documentId,
    mySchools,
  ]);

  return {
    animationProps,
    mySchools,
    schoolContext,
    authentication,
    shouldShowSchoolSelection,
    handleSchoolChange,
    matLogo,
  };
};
